import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './i18n';
import './styles/accessibility.css'; // Add accessibility styles
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import CoursesPage from './pages/CoursesPage';
import EventsPage from './pages/EventsPage';
import AboutUsPage from './pages/AboutUsPage';
import DonatePage from './pages/DonatePage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import TopStudentsPage from './pages/TopStudentsPage';
import PostsPage from './pages/PostsPage';
import PostDetailPage from './pages/PostDetailPage';
import ProtectedRoute from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { forceScrollToElement } from './services/scrollService';
import FullscreenLoader from './components/FullscreenLoader';
import { ToastProvider } from './components/ui/ToastProvider';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { PageLoader } from './components/ui/EnhancedSkeletonLoader';
import { SkipLink } from './utils/focusManagement';
import BottomNavigation from './components/BottomNavigation';

// Code splitting for better performance
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const TeacherDashboard = React.lazy(() => import('./pages/TeacherDashboard'));
const StudentDashboard = React.lazy(() => import('./pages/StudentDashboard'));

// ScrollToHashElement component to handle hash navigation
function ScrollToHashElement() {
  const location = useLocation();
  
  useEffect(() => {
    // If there's a hash in the URL
    if (location.hash) {
      // Extract the element ID from the hash (remove the # character)
      const elementId = location.hash.substring(1);
      
      // Wait a bit for the DOM to fully render
      setTimeout(() => {
        // Try to scroll to the element
        forceScrollToElement(elementId, { offset: 80 });
      }, 300);
    } else {
      // If no hash, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location]); // Re-run when location changes
  
  return null; // This component doesn't render anything
}

function App() {
  const [pageLoaded, setPageLoaded] = useState(false);
  const [minDelayDone, setMinDelayDone] = useState(false);

  // Show loader until initial load + short delay to avoid flicker
  useEffect(() => {
    const handleLoad = () => setPageLoaded(true);
    // If DOM is already ready, mark as loaded
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      setPageLoaded(true);
    }

    window.addEventListener('load', handleLoad);

    // Minimum visible time to prevent flicker
    const delay = setTimeout(() => setMinDelayDone(true), 900);
    // Fallback to ensure loader never sticks indefinitely
    const hardStop = setTimeout(() => {
      setPageLoaded(true);
      setMinDelayDone(true);
    }, 4000);

    return () => {
      window.removeEventListener('load', handleLoad);
      clearTimeout(delay);
      clearTimeout(hardStop);
    };
  }, []);

  const showLoader = !(pageLoaded && minDelayDone);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          {/* Skip to main content link for keyboard users */}
          <SkipLink targetId="main-content" />
          
          {showLoader && <FullscreenLoader />}
          {/* Add scroll handler component */}
          <ScrollToHashElement />
          <main id="main-content" role="main" className="main-content-with-bottom-nav">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/scholarships" element={<EventsPage />} />
              <Route path="/events" element={<Navigate to="/scholarships" replace />} />
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/top-students" element={<TopStudentsPage />} />
              <Route path="/news" element={<PostsPage />} />
              <Route path="/news/:id" element={<PostDetailPage />} />
              <Route path="/donate" element={<DonatePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route 
                path="/admin/*" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <AdminDashboard />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/teacher/*" 
                element={
                  <ProtectedRoute allowedRoles={['teacher']}>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <TeacherDashboard />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/student/*" 
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <StudentDashboard />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                } 
              />
              {/* Catch-all route for 404 errors */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          
          {/* Bottom Navigation for mobile */}
          <BottomNavigation />
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
